import { PhoneNumber } from "models/domain/phoneNumber.domain";
import { Zipcode } from "models/domain/zipcode.domain";
import * as yup from "yup";


export const postSchema = yup.object({
    type: yup.string()
        .required('Tipo de postagem é obrigatório.'),
    title: yup.string()
        .required('Título é obrigatório.')
        .min(3, { message: 'É obrigatório no mínimo 3 caracteres.' })
        .max(50, { message: 'Máximo de 50 caracteres' }),
    description: yup.string()
        .required('Descrição é obrigatória.')
        .min(3, 'Descrição muito curta.')
        .max(250, 'Descrição muito longa. Máximo de 250 caracteres.'),
    photo: yup.mixed().optional(),
    phoneNumber: yup.string()
        .optional()
        .test('check phone is valid',
            'Número de telefone inválido.',
            item => {
                if (!item) {
                    return true;
                }
                try {
                    PhoneNumber.fromString(item ?? '');
                    return true;
                } catch {
                    return false
                }
            }),
    address: yup.object({
        zipcode: yup.string()
            .test('check zipcode is valid',
                'CEP inválido.',
                item => {
                    if (!item) {
                        return true;
                    }
                    try {
                        Zipcode.fromString(item ?? '');
                        return true;
                    } catch {
                        return false
                    }
                })
            .optional(),
        streetName: yup.string()
            .required('Logradouro é obrigatório')
            .max(100, 'Máximo de 100 caracteres.'),
        streetType: yup.string()
            .required('Tipo de Logradouro é obrigatório')
            .max(100, 'Máximo de 100 caracteres.'),
        addressNumber: yup.string()
            .required('Número é obrigatório')
            .max(30, 'Máximo de 30 caracteres.'),
        addressComplement: yup.string()
            .max(100, 'Máximo de 100 caracteres.')
            .optional(),
        neighborhood: yup.string()
            .required('Bairro é obrigatório')
            .max(100, 'Máximo de 100 caracteres.'),
        city: yup.string()
            .required('Município é obrigatório')
            .max(100, 'Máximo de 100 caracteres.'),
        state: yup.string()
            .required('Estado é obrigatório')
            .max(100, 'Máximo de 100 caracteres.'),
    }).optional(),
    startTime: yup.string()
        .matches(/\d{2}:\d{2}/g, { message: 'Horário inicial inválido' })
        .optional(),
    endTime: yup.string()
        .matches(/\d{2}:\d{2}/g, { message: 'Horário final inválido' })
        .optional(),
    position: yup.object({
        latitude: yup.number()
            .required('Selecione um ponto no mapa.')
            .typeError('Selecione um ponto no mapa.')
            .min(-90, 'Latitude mínima é -90º')
            .max(90, 'Latitude máxima é 90º'),
        longitude: yup.number()
            .required('Selecione um ponto no mapa.')
            .typeError('Selecione um ponto no mapa.')
            .min(-180, 'Longitude mínima é -180º')
            .max(180, 'Longitude máxima é 180º'),
    }).required('Selecione um ponto no mapa')
});

export type PostForm = {
    type: string;
    title: string;
    description: string;
    photo?: File;
    position: {
        latitude: number;
        longitude: number;
    };
    address: {
        streetName: string;
        streetType: string;
        addressNumber: string;
        addressComplement?: string;
        neighborhood: string;
        city: string;
        state: string;
        zipcode: string;
    },
    phoneNumber?: string;
    startTime?: string;
    endTime?: string;
};

function parseToPontoEssencial(data: PostForm) {    
    const [startHours, startMinutes] = data.startTime!.split(':');
    const [endHours, endMinutes] = data.endTime!.split(':')
    return {
        imagem: typeof data.photo !== "string" ? data.photo : undefined,
        titulo: data.title,
        descricao: data.description,
        latitude: data.position.latitude,
        longitude: data.position.longitude,
        fullTime: false,
        horaAbertura: { hora: +startHours, minuto: +startMinutes },
        horaFechamento: { hora: +endHours, minuto: +endMinutes },
        telefone: data.phoneNumber,
        endereco: {
            bairro: data.address.neighborhood,
            cep: data.address.zipcode,
            complemento: data.address?.addressComplement,
            logradouro: data.address.streetName,
            municipio: data.address.city,
            numero: data.address.addressNumber,
            tipoLogradouro: data.address.streetType,
            uf: data.address.state
        }
    }
}

function parseToInformativo(data: PostForm) {
    return {
        imagem: typeof data.photo !== "string" ? data.photo : undefined,
        titulo: data.title,
        descricao: data.description,
        latitude: data.position.latitude,
        longitude: data.position.longitude,
        fullTime: undefined,
        horaAbertura: undefined,
        horaFechamento: undefined,
        telefone: undefined,
        endereco: {
            bairro: data.address.neighborhood,
            cep: data.address.zipcode,
            complemento: data.address?.addressComplement,
            logradouro: data.address.streetName,
            municipio: data.address.city,
            numero: data.address.addressNumber,
            tipoLogradouro: data.address.streetType,
            uf: data.address.state
        }
    }
}

export const parsePostForm = {
    "INFORMATIVO": parseToInformativo,
    "PONTO_ESSENCIAL": parseToPontoEssencial
};